import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Search, ChevronLeft, ChevronRight, X, Heart, ShoppingBasket } from "lucide-react";
import { api, mediaSrc, money, formatApiError } from "@/lib/api";
import { getCart, addToCart } from "@/lib/cart";

const THEMES = ["Star Wars", "DC", "Marvel", "POTC", "Retired", "Disney", "Harry Potter", "LOTR", "Other"];
const RARITY_TIERS = ["Popular", "Rare", "Special Piece", "Limited Run", "Ultra Rare", "Grail", "Vault Holy Grail"];

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [theme, setTheme] = useState("All");
  const [rarity, setRarity] = useState("All");
  const [sort, setSort] = useState("featured");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem("sx_page_size")) || 6);
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sx_wishlist")) || []; } catch { return []; }
  });
  const [wishlistOnly, setWishlistOnly] = useState(false);
  const [cart, setCartState] = useState(() => getCart());
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const sync = () => setCartState(getCart());
    window.addEventListener("sx-cart", sync);
    return () => window.removeEventListener("sx-cart", sync);
  }, []);

  useEffect(() => {
    const open = (e) => {
      const item = items.find((i) => i.id === e.detail);
      if (item) { setSelected(item); setPhotoIdx(0); }
    };
    window.addEventListener("open-item-detail", open);
    return () => window.removeEventListener("open-item-detail", open);
  }, [items]);

  const addBasket = (item) => {
    if (addToCart(item)) toast.success("Added to your basket");
    else toast.info("Already in your basket");
  };

  const toggleWish = (id) =>
    setWishlist((w) => (w.includes(id) ? w.filter((x) => x !== id) : [...w, id]));

  useEffect(() => {
    localStorage.setItem("sx_page_size", String(pageSize));
  }, [pageSize]);

  useEffect(() => {
    localStorage.setItem("sx_wishlist", JSON.stringify(wishlist));
  }, [wishlist]);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [buyingId, setBuyingId] = useState(null);

  const goToPage = (n) => {
    setPage(n);
    document.getElementById("stock")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const isJustLanded = (item) =>
    item.created_at && Date.now() - new Date(item.created_at).getTime() < 7 * 24 * 60 * 60 * 1000;

  useEffect(() => {
    setPage(1);
  }, [filter, theme, rarity, sort, query, pageSize, wishlistOnly]);

  useEffect(() => {
    api.get("/items")
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Could not load stock"))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ["All", ...new Set(items.map((i) => i.category).filter(Boolean))], [items]);

  const visible = useMemo(() => {
    let list = items.filter((i) => i.in_stock);
    if (wishlistOnly) list = list.filter((i) => wishlist.includes(i.id));
    if (filter !== "All") list = list.filter((i) => i.category === filter);
    if (theme !== "All") list = list.filter((i) => (i.theme || "Other") === theme);
    if (rarity !== "All") list = list.filter((i) => i.rarity === rarity);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((i) =>
        [i.title, i.description, i.category, i.theme, i.rarity, i.condition]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    return list;
  }, [items, filter, theme, rarity, sort, query, wishlistOnly, wishlist]);

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageItems = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const soldItems = useMemo(() => items.filter((i) => !i.in_stock), [items]);
  const SOLD_PER_VIEW = 3;
  const [soldPage, setSoldPage] = useState(0);
  const [stripPaused, setStripPaused] = useState(false);
  const soldPages = Math.max(1, Math.ceil(soldItems.length / SOLD_PER_VIEW));
  const currentSoldPage = Math.min(soldPage, soldPages - 1);
  const soldView = soldItems.slice(currentSoldPage * SOLD_PER_VIEW, currentSoldPage * SOLD_PER_VIEW + SOLD_PER_VIEW);

  const goSoldPage = (n) => setSoldPage(((n % soldPages) + soldPages) % soldPages);

  useEffect(() => {
    if (soldPages < 2) return;
    const id = setInterval(() => {
      if (!stripPaused) setSoldPage((p) => (p + 1) % soldPages);
    }, 5000);
    return () => clearInterval(id);
  }, [soldPages, stripPaused]);

  const buy = async (item) => {
    setBuyingId(item.id);
    try {
      const { data } = await api.post("/payments/checkout", { item_id: item.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e, "Checkout failed. Please try again."));
      setBuyingId(null);
    }
  };

  const selectedPhotos = selected
    ? selected.images?.length
      ? selected.images
      : selected.image_url
        ? [selected.image_url]
        : []
    : [];

  return (
    <section id="stock" className="max-w-7xl mx-auto px-6 py-24 md:py-32 scroll-mt-20" data-testid="inventory-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className=""
      >
        <div className="max-w-md">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#B38728] flex items-center gap-3">
            <span className="inline-block w-10 h-px bg-[#B38728]" /> 02 — The Vault
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-5">Current Stock</h2>
          <p className="text-[#5C564C] text-sm mt-3">Authenticated pieces, ready to ship. Stock moves fast — once it's gone, it's gone.</p>
        </div>

        <div className="mt-10 space-y-3" data-testid="stock-controls">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2" data-testid="category-pills">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`filter-tab-${c.toLowerCase()}`}
                  className={`px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${filter === c ? "bg-[#0A0A0A] text-[#F3EDE3] border-[#0A0A0A]" : "border-[#DCD3C3] text-[#5C564C] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <button
              onClick={() => setWishlistOnly((v) => !v)}
              data-testid="wishlist-toggle"
              className={`flex items-center gap-2 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${wishlistOnly ? "bg-[#B38728] text-[#0A0A0A] border-[#B38728]" : "border-[#DCD3C3] text-[#5C564C] hover:border-[#B38728] hover:text-[#B38728]"}`}
            >
              <Heart className={`w-3.5 h-3.5 ${wishlistOnly ? "fill-current" : ""}`} />
              Saved ({wishlist.length})
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 border border-[#DCD3C3] bg-white p-2.5">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#B38728] pointer-events-none" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the vault…"
                data-testid="stock-search-input"
                className="w-full bg-transparent border border-transparent pl-9 pr-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#0A0A0A] placeholder-[#a79f90] focus:outline-none focus:border-[#B38728] transition-colors"
              />
            </div>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              data-testid="theme-select"
              className="bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#5C564C] focus:outline-none focus:border-[#B38728] cursor-pointer"
            >
              <option value="All">All Themes</option>
              {THEMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select
              value={rarity}
              onChange={(e) => setRarity(e.target.value)}
              data-testid="rarity-select"
              className="bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#5C564C] focus:outline-none focus:border-[#B38728] cursor-pointer"
            >
              <option value="All">All Rarities</option>
              {RARITY_TIERS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="sort-select"
              className="bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#5C564C] focus:outline-none focus:border-[#B38728] cursor-pointer"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price ↑</option>
              <option value="price-desc">Price ↓</option>
            </select>
            <div className="flex items-center border border-[#DCD3C3] bg-[#F3EDE3]" data-testid="page-size-toggle">
              <span className="pl-3 pr-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#a79f90]">Show</span>
              {[6, 9, 12].map((n) => (
                <button
                  key={n}
                  onClick={() => setPageSize(n)}
                  data-testid={`page-size-${n}`}
                  className={`px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${pageSize === n ? "bg-[#0A0A0A] text-[#F3EDE3]" : "text-[#5C564C] hover:text-[#0A0A0A]"}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {loading ? (
        <div className="py-24 flex justify-center" data-testid="inventory-loading">
          <Loader2 className="w-8 h-8 animate-spin text-[#B38728]" />
        </div>
      ) : (
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="inventory-grid">
          {pageItems.map((item, i) => (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="gold-border-glow bg-white border border-[#DCD3C3] group cursor-pointer"
              data-testid={`item-card-${item.id}`}
              onClick={() => { setSelected(item); setPhotoIdx(0); }}
            >
              <div className="relative overflow-hidden">
                {item.image_url ? (
                  <img
                    src={mediaSrc(item.image_url)}
                    alt={item.title}
                    className="w-full aspect-[4/5] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                ) : (
                  <div className="w-full aspect-[4/5] bg-[#EAE2D5] flex items-center justify-center font-mono text-xs text-[#5C564C]">PHOTO PENDING</div>
                )}
                {!item.in_stock && (
                  <div className="absolute inset-0 bg-[#0A0A0A]/70 flex items-center justify-center">
                    <span className="font-mono text-sm uppercase tracking-[0.4em] text-[#F3EDE3] border border-[#F3EDE3] px-6 py-2">Sold</span>
                  </div>
                )}
                {item.rarity && (
                  <span className="absolute top-4 left-4 bg-[#121110] text-[#D4AF37] font-mono text-[9px] uppercase tracking-[0.25em] px-3 py-1.5">
                    {item.rarity}
                  </span>
                )}
                {isJustLanded(item) && item.in_stock && (
                  <span className="absolute top-4 right-4 bg-[#D4AF37] text-[#0A0A0A] font-mono text-[9px] uppercase tracking-[0.25em] px-3 py-1.5 shadow-lg" data-testid={`just-landed-${item.id}`}>
                    Just Landed
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleWish(item.id); }}
                  data-testid={`wishlist-heart-${item.id}`}
                  aria-label={wishlist.includes(item.id) ? "Remove from saved" : "Save for later"}
                  className={`absolute bottom-3 right-3 rounded-full p-2.5 shadow-md transition-colors ${wishlist.includes(item.id) ? "bg-[#D4AF37] text-[#0A0A0A]" : "bg-white/90 text-[#5C564C] hover:text-[#B38728]"}`}
                >
                  <Heart className={`w-4 h-4 ${wishlist.includes(item.id) ? "fill-current" : ""}`} />
                </button>
              </div>
              <div className="p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728]">
                  {item.category}{item.theme && item.theme !== "Other" ? ` · ${item.theme}` : ""}
                </p>
                <h3 className="font-serif text-lg sm:text-xl mt-2 leading-snug">{item.title}</h3>
                {item.condition && (
                  <p className="flex items-center gap-2 text-xs text-[#5C564C] mt-2">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#B38728]" /> {item.condition}
                  </p>
                )}
                {item.description && <p className="text-sm text-[#5C564C] mt-3 leading-relaxed line-clamp-2">{item.description}</p>}
                <div className="mt-6 flex items-center justify-between gap-2">
                  <p className="font-serif text-2xl" data-testid={`item-price-${item.id}`}>{money(item.price)}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); addBasket(item); }}
                      disabled={!item.in_stock}
                      data-testid={`add-basket-${item.id}`}
                      className={`p-3 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${cart.some((c) => c.id === item.id) ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]" : "border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#F3EDE3]"}`}
                      aria-label="Add to basket"
                    >
                      <ShoppingBasket className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); buy(item); }}
                      disabled={!item.in_stock || buyingId === item.id}
                      data-testid={`buy-button-${item.id}`}
                      className="bg-[#0A0A0A] text-[#F3EDE3] px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {buyingId === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      {item.in_stock ? (buyingId === item.id ? "Opening…" : "Buy Now") : "Sold"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
          {visible.length === 0 && (
            <p className="col-span-full text-center text-[#5C564C] py-16 text-sm" data-testid="inventory-empty">
              {wishlistOnly
                ? "No saved pieces yet — tap the heart on anything you love and it'll wait for you here."
                : "Nothing matches right now — try a different search or theme, or follow our socials for drops."}
            </p>
          )}
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div className="mt-12 flex flex-col items-center gap-4" data-testid="stock-pagination">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]" data-testid="stock-count">
            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, visible.length)} of {visible.length} pieces
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                data-testid="pagination-prev"
                className="p-2.5 border border-[#DCD3C3] text-[#5C564C] hover:border-[#0A0A0A] hover:text-[#0A0A0A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => goToPage(n)}
                  data-testid={`pagination-page-${n}`}
                  className={`w-10 h-10 font-mono text-xs border transition-colors ${currentPage === n ? "bg-[#0A0A0A] text-[#F3EDE3] border-[#0A0A0A]" : "border-[#DCD3C3] text-[#5C564C] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"}`}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                data-testid="pagination-next"
                className="p-2.5 border border-[#DCD3C3] text-[#5C564C] hover:border-[#0A0A0A] hover:text-[#0A0A0A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {!loading && soldItems.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 border-t border-[#DCD3C3] pt-8"
          data-testid="sold-strip"
        >
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#B38728]">Recently Sold</p>
              <h3 className="font-serif text-lg mt-1.5">Gone from the vault — every piece finds its collector</h3>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => goSoldPage(currentSoldPage - 1)} data-testid="sold-prev" aria-label="Previous sold items" className="p-2.5 border border-[#DCD3C3] text-[#5C564C] hover:border-[#0A0A0A] hover:text-[#0A0A0A] transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => goSoldPage(currentSoldPage + 1)} data-testid="sold-next" aria-label="Next sold items" className="p-2.5 border border-[#DCD3C3] text-[#5C564C] hover:border-[#0A0A0A] hover:text-[#0A0A0A] transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div
            onMouseEnter={() => setStripPaused(true)}
            onMouseLeave={() => setStripPaused(false)}
            className="mt-4"
            data-testid="sold-strip-track"
          >
            <motion.div
              key={currentSoldPage}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-3 gap-2 sm:gap-4"
            >
              {soldView.map((item) => (
                <div key={item.id} data-sold-card data-testid={`sold-card-${item.id}`} className="bg-white border border-[#DCD3C3] flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-2 sm:p-3 min-w-0">
                  <div className="relative overflow-hidden shrink-0 w-full h-16 sm:w-20 sm:h-20">
                    {item.image_url ? (
                      <img src={mediaSrc(item.image_url)} alt={item.title} className="w-full h-full object-cover grayscale-[40%]" />
                    ) : (
                      <div className="w-full h-full bg-[#EAE2D5]" />
                    )}
                    <span className="absolute top-1 left-1 bg-[#121110] text-[#D4AF37] font-mono text-[7px] uppercase tracking-[0.2em] px-1.5 py-0.5">Sold</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-serif text-xs sm:text-sm leading-snug line-clamp-2">{item.title}</p>
                    <p className="font-mono text-[10px] text-[#5C564C] mt-1 tracking-widest">{money(item.price)}</p>
                  </div>
                </div>
              ))}
            </motion.div>
            {soldPages > 1 && (
              <div className="mt-5 flex justify-center gap-1.5" data-testid="sold-dots">
                {Array.from({ length: soldPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => goSoldPage(i)}
                    data-testid={`sold-dot-${i}`}
                    aria-label={`Sold page ${i + 1}`}
                    className={`w-2 h-2 rounded-full transition-colors ${i === currentSoldPage ? "bg-[#B38728]" : "bg-[#DCD3C3] hover:bg-[#B38728]/60"}`}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {selected && (        <div
          className="fixed inset-0 z-[70] bg-[#0A0A0A]/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="item-detail-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-[#DCD3C3] w-full max-w-3xl max-h-[90vh] overflow-y-auto grid md:grid-cols-2"
          >
            <div className="relative bg-[#EAE2D5]">
              {selectedPhotos.length > 0 ? (
                <>
                  <img
                    src={mediaSrc(selectedPhotos[Math.min(photoIdx, selectedPhotos.length - 1)])}
                    alt={selected.title}
                    className="w-full h-full min-h-[20rem] object-cover"
                    data-testid="item-detail-image"
                  />
                  {selectedPhotos.length > 1 && (
                    <>
                      <button
                        onClick={() => setPhotoIdx((i) => (i - 1 + selectedPhotos.length) % selectedPhotos.length)}
                        data-testid="detail-photo-prev"
                        aria-label="Previous photo"
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#0A0A0A]/70 text-[#F3EDE3] p-2.5 hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setPhotoIdx((i) => (i + 1) % selectedPhotos.length)}
                        data-testid="detail-photo-next"
                        aria-label="Next photo"
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#0A0A0A]/70 text-[#F3EDE3] p-2.5 hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="detail-photo-dots">
                        {selectedPhotos.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setPhotoIdx(i)}
                            data-testid={`detail-photo-dot-${i}`}
                            aria-label={`Photo ${i + 1}`}
                            className={`w-2 h-2 rounded-full transition-colors ${i === Math.min(photoIdx, selectedPhotos.length - 1) ? "bg-[#D4AF37]" : "bg-white/60 hover:bg-white"}`}
                          />
                        ))}
                      </div>
                      <span className="absolute top-4 left-4 bg-[#0A0A0A]/70 text-[#F3EDE3] font-mono text-[9px] uppercase tracking-[0.25em] px-2.5 py-1" data-testid="detail-photo-counter">
                        {Math.min(photoIdx, selectedPhotos.length - 1) + 1} / {selectedPhotos.length}
                      </span>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full min-h-[20rem] flex items-center justify-center font-mono text-xs text-[#5C564C]">PHOTO PENDING</div>
              )}
              {isJustLanded(selected) && selected.in_stock && (
                <span className="absolute top-4 right-4 bg-[#D4AF37] text-[#0A0A0A] font-mono text-[9px] uppercase tracking-[0.25em] px-3 py-1.5 shadow-lg">
                  Just Landed
                </span>
              )}
            </div>
            <div className="p-8 flex flex-col">
              <div className="flex items-start justify-between gap-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728]">
                  {selected.category}{selected.theme && selected.theme !== "Other" ? ` · ${selected.theme}` : ""}
                </p>
                <button onClick={() => setSelected(null)} data-testid="item-detail-close-button" className="text-[#5C564C] hover:text-[#0A0A0A] transition-colors" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl mt-3 leading-snug" data-testid="item-detail-title">{selected.title}</h3>
              <div className="mt-5 space-y-3 text-sm">
                {selected.rarity && (
                  <p className="flex items-center gap-2 text-[#5C564C]">
                    <span className="bg-[#121110] text-[#D4AF37] font-mono text-[9px] uppercase tracking-[0.25em] px-3 py-1">{selected.rarity}</span>
                  </p>
                )}
                {selected.condition && (
                  <p className="flex items-center gap-2 text-[#5C564C]">
                    <ShieldCheck className="w-4 h-4 text-[#B38728] shrink-0" /> {selected.condition}
                  </p>
                )}
                {selected.description && <p className="text-[#5C564C] leading-relaxed" data-testid="item-detail-description">{selected.description}</p>}
              </div>
              <div className="mt-auto pt-8 flex items-center justify-between gap-4 border-t border-[#DCD3C3]">
                <p className="font-serif text-3xl" data-testid="item-detail-price">{money(selected.price)}</p>
                <button
                  onClick={() => toggleWish(selected.id)}
                  data-testid="item-detail-wishlist-button"
                  aria-label={wishlist.includes(selected.id) ? "Remove from saved" : "Save for later"}
                  className={`p-4 border transition-colors ${wishlist.includes(selected.id) ? "border-[#D4AF37] text-[#B38728]" : "border-[#DCD3C3] text-[#5C564C] hover:border-[#B38728] hover:text-[#B38728]"}`}
                >
                  <Heart className={`w-4 h-4 ${wishlist.includes(selected.id) ? "fill-current" : ""}`} />
                </button>
                <button
                  onClick={() => addBasket(selected)}
                  disabled={!selected.in_stock}
                  data-testid="item-detail-add-basket"
                  aria-label="Add to basket"
                  className={`p-4 border transition-colors disabled:opacity-40 ${cart.some((c) => c.id === selected.id) ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]" : "border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#F3EDE3]"}`}
                >
                  <ShoppingBasket className="w-4 h-4" />
                </button>
                <button
                  onClick={() => buy(selected)}
                  disabled={!selected.in_stock || buyingId === selected.id}
                  data-testid="item-detail-buy-button"
                  className="bg-[#0A0A0A] text-[#F3EDE3] px-8 py-4 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {buyingId === selected.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {selected.in_stock ? (buyingId === selected.id ? "Opening…" : "Buy Now") : "Sold"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
}